/** Projekt-Helfer der Projektübersicht — 1:1 aus apps/vimaana-desktop übernommen.
 *
 * Quellen: ProjektePage.tsx (Status-Kanonisierung, KPI-/Gewinnformeln,
 * Jahresoptionen), lib/projektStatusSlug.ts und lib/kundeAdresse.ts. Bewusst
 * eine eigene Datei statt planer.ts: planer.ts gehört der Planungsansicht,
 * hier steht ausschließlich Projektlogik.
 */

/** Reihenfolge der Status-Gruppen (Bauprojekte). */
export const STATUS_ORDER = [
  "🟢 aktiv",
  "🟡 anfrage",
  "🔵 rechnung",
  "⚫ abgeschlossen",
  "🔴 verloren",
];

/**
 * Pflegeprojekte brauchen nur einen reduzierten Lebenszyklus
 * (aktiv → Rechnung → abgeschlossen) — Parität mit planer-mobile.
 */
export const PFLEGE_STATUS_ORDER = ["🟢 aktiv", "🔵 rechnung", "⚫ abgeschlossen"];

const AKTIV_STATUS_KEY = "🟢 aktiv";

/**
 * Der Name eines Pflegeprojekts wird nicht erfunden, er folgt einem Muster:
 * „Pflege <Kunde> · <Monat Jahr>" — dasselbe, das der Planer beim Anlegen eines
 * Pflegetermins vergibt (apps/vimaana-planer-mobile, MNewTerminSheet). So heißen
 * alle Pflegeprojekte gleich, egal wo sie entstehen.
 */
export function pflegeTitel(kundeName: string, datumIso = ""): string {
  const d = datumIso ? new Date(datumIso + "T12:00:00") : new Date();
  const monat = (Number.isNaN(d.getTime()) ? new Date() : d).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  return `Pflege ${(kundeName || "").trim() || "Kunde"} · ${monat}`;
}

/** Beim Öffnen ist nur „Aktiv“ ausgeklappt. */
export function buildDefaultCollapsed(): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const s of STATUS_ORDER) {
    if (s !== AKTIV_STATUS_KEY) out[s] = true;
  }
  out["🔴 verloren"] = true;
  out.pausiert = true;
  return out;
}

export function calendarYearNow(): number {
  return new Date().getFullYear();
}

/** „Alle Jahre“ plus die letzten 13 Kalenderjahre. */
export function buildYearOptions(): Array<number | "all"> {
  const y = calendarYearNow();
  const years: Array<number | "all"> = ["all"];
  for (let i = y; i >= y - 12; i--) years.push(i);
  return years;
}

const STATUS_ALIASES: Record<string, string> = {
  "🔴 storniert": "🔴 verloren",
};

/** Excel / alte Defaults ohne Emoji — muss zu crm/config.yaml status_options passen. */
const LEGACY_STATUS_TO_CANONICAL: Record<string, string> = {
  Anfrage: "🟡 anfrage",
  Angebot: "🟡 anfrage",
  Aktiv: "🟢 aktiv",
  Rechnung: "🔵 rechnung",
  Abgeschlossen: "⚫ abgeschlossen",
  Verloren: "🔴 verloren",
};

/**
 * Einheitlicher Status für Gruppierung + Select: verhindert „Anfrage“ in der
 * Sektion, Dropdown aber „aktiv“ (weil kein <option> zum DB-Wert passt).
 */
export function canonicalProjektStatus(raw: string, allowed: string[]): string {
  const t = (raw || "").trim();
  const base = t || "Unbekannt";
  const s = STATUS_ALIASES[base] || base;
  if (allowed.length > 0 && allowed.includes(s)) return s;
  const legacy =
    LEGACY_STATUS_TO_CANONICAL[s] ??
    LEGACY_STATUS_TO_CANONICAL[base] ??
    Object.entries(LEGACY_STATUS_TO_CANONICAL).find(
      ([k]) => k.toLowerCase() === base.toLowerCase(),
    )?.[1];
  if (legacy && (allowed.length === 0 || allowed.includes(legacy))) return legacy;
  return s;
}

/** Status-Slug ohne Emoji für CSS-Klassen — `anfrage`, `aktiv`, `rechnung`, … */
export function statusIdFor(raw: string): string {
  const s = (raw || "")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
  return s || "unbekannt";
}

/** Reihenfolge im KPI-Strip / Dropdown. */
export const STATUS_ID_ORDER: string[] = [
  "anfrage",
  "aktiv",
  "rechnung",
  "abgeschlossen",
  "verloren",
  "pausiert",
];

export const STATUS_LABEL: Record<string, string> = {
  anfrage: "Anfrage",
  aktiv: "Aktiv",
  rechnung: "Rechnung",
  abgeschlossen: "Abgeschlossen",
  verloren: "Verloren",
  pausiert: "Pausiert",
  unbekannt: "Unbekannt",
};

/** Gewinn = Nettowert − Material − Personal (best effort, fehlende Werte = 0). */
export function computeGewinn(p: {
  nettowert?: number | null;
  materialkosten?: number | null;
  personalkosten?: number | null;
}): number {
  const netto = Number(p.nettowert || 0);
  if (netto <= 0) return 0;
  const g = netto - Number(p.materialkosten || 0) - Number(p.personalkosten || 0);
  return g > 0 ? g : 0;
}

const EUR_FMT_INT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });

/** Ganze Euro ohne Nachkommastellen; 0/leer → null, damit die Karte nichts rendert. */
export function fmtEUR(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n) || n === 0) return null;
  return EUR_FMT_INT.format(n) + " €";
}

export function initialsFromName(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Sichere Vergleichskette für die Suche — API/Cache kann Felder als null liefern. */
export function normSearchField(v: unknown): string {
  return String(v ?? "").toLowerCase();
}

/**
 * Volle, einzeilige Kundenadresse — gleiche Regel wie das Backend
 * (`_compose_adresse`). Das Projekt bekommt damit den konkreten Ort
 * (Straße + PLZ + Stadt) statt nur der Stadt.
 *
 * Der Kontor-Kundenendpunkt liefert `adresse` bereits fertig; die Berechnung
 * hier ist der Fallback für ältere Datensätze ohne dieses Feld.
 */
export function composeKundeAdresse(k: {
  adresse?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
  lieferadresse?: string;
}): string {
  const fertig = (k.adresse || "").trim();
  if (fertig) return fertig;

  const strasse = (k.strasse || "").trim();
  const ortZeile = [(k.plz || "").trim(), (k.ort || "").trim()].filter(Boolean).join(" ").trim();
  if (strasse && ortZeile) return `${strasse}, ${ortZeile}`;
  if (strasse) return strasse;

  const liefer = (k.lieferadresse || "").trim();
  if (liefer) {
    return liefer
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .join(", ");
  }
  return (k.ort || "").trim();
}

/** Aus einem Kunden-Feld wie „Max Mustermann (mail@…)“ Name + Mail raten. */
export function parseKundeQueryInput(raw: string): { nameGuess: string; emailGuess: string } {
  const t = raw.trim();
  const m = t.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (m) {
    const namePart = m[1].trim();
    const inParen = m[2].trim();
    return { nameGuess: namePart || t, emailGuess: inParen.includes("@") ? inParen : "" };
  }
  return { nameGuess: t, emailGuess: "" };
}


/**
 * Projektstatus ohne Emoji — der Wert in der Datenbank bleibt „🟢 aktiv", gezeigt
 * werden Wort und ein Punkt in der Farbe der App (Benjamin 03.10.2026: die
 * Emoji-Kreise passen nicht ins Design).
 */
export function statusWort(status: string): string {
  const wort = (status || "").replace(/^[^\p{L}]+/u, "").trim();
  return wort ? wort.charAt(0).toUpperCase() + wort.slice(1) : "—";
}

/** Ton für den Statuspunkt: gruen · gelb · blau · grau · rot. */
export function statusTon(status: string): "gruen" | "gelb" | "blau" | "grau" | "rot" {
  const s = (status || "").toLowerCase();
  if (s.includes("verloren")) return "rot";
  if (s.includes("abgeschlossen")) return "grau";
  if (s.includes("rechnung")) return "blau";
  if (s.includes("anfrage")) return "gelb";
  return "gruen";
}
